import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  Snackbar,
  Box,
  List,
  Menu,
  MenuItem,
  useMediaQuery
} from "@mui/material";
import ModeCommentIcon from "@mui/icons-material/ModeComment";
import ResumeDetailsModal from "./ResumeDetailsModal";
import Loader from "../../common/Loader";
import { Postrequestcall } from "../../request/Postrequest";
import { FETCH_USERS_RESUMES, DELETE_USERS_RESUMES } from "../../constant/Apipath";
import { useLocation, useNavigate } from "react-router-dom";
import DeleteConfirmation from "../common/DeleteConfirmation";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Pageheader from "../common/Pageheader";
import Emptydata from "../common/Emptydata";
import { useTheme } from "@emotion/react";
import { AddButton } from "../common/AddButton";
import { English } from "../../constant/EnglishFallback";
import AdContainer from "../common/AdContainer";

export default function ResumeBuilder() {
  const { loginData, multiLanguageData } = useSelector((state) => state?.main);
  const currentUserId = loginData?.userInformation?.UserId ?? 0;
  const [userResumes, setUserResumes] = useState([]);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedResume, setSelectedResume] = useState(null);
  const [openResumeDetailsModal, setOpenResumeDetailsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const LanguageData = multiLanguageData?.ResumeBuilder || {};
  const {
    TEXT_RESUME_BUILDER_HEADER = English.ResumeBuilder.TEXT_RESUME_BUILDER_HEADER,
    TEXT_RESUME_INFO_HEADER = English.ResumeBuilder.TEXT_RESUME_INFO_HEADER,
    TEXT_NO_RESUME_LINE1 = English.ResumeBuilder.TEXT_NO_RESUME_LINE1,
    TEXT_NO_RESUME_LINE2 = English.ResumeBuilder.TEXT_NO_RESUME_LINE2,
    TEXT_ADD_RESUME = English.ResumeBuilder.TEXT_ADD_RESUME,
    TEXT_MENU_DELETE_BTN = English.ResumeBuilder.TEXT_MENU_DELETE_BTN,
    TEXT_CONFIRM_DELETE = English.ResumeBuilder.TEXT_CONFIRM_DELETE,
    TEXT_DELETE_THIS_RESUME = English.ResumeBuilder.TEXT_DELETE_THIS_RESUME,
    TEXT_DELETE_SNACKBAR = English.ResumeBuilder.TEXT_DELETE_SNACKBAR
  } = LanguageData || {};

  const headerName =
    location.pathname === "/resume-builder" ? TEXT_RESUME_BUILDER_HEADER : TEXT_RESUME_INFO_HEADER;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const fetchResumes = async () => {
    try {
      setIsLoading(true);
      const obj = {
        PageNr: 1,
        NrOfRecPerPage: 100,
        FullSearch: "",
        UserId: currentUserId,
        IncludeRecordNr: "true",
        TypeofObjectReturned: "",
        SearchList: [{ UserId: currentUserId }]
      };

      const response = await Postrequestcall(FETCH_USERS_RESUMES, obj, loginData?.accessToken);
      const uniqueResumes = response?.data?.data?.filter(
        (resume, index, self) => index === self.findIndex((r) => r.id === resume.id)
      );
      setUserResumes(uniqueResumes);
    } catch (error) {
      console.error("fetchResumes-> Error fetching resumes: ", error);
      setError("Failed to load resumes. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchResumes();
  }, []);

  const handleAddClick = () => {
    navigate("add-resume");
  };

  const handleDeleteResume = () => {
    setOpenDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedResume) return;
    setOpenDeleteModal(false);
    try {
      const deleteData = {
        userId: Number(currentUserId),
        ReturnRecordId: false,
        ReturnRecordError: true,
        DeleteList: [{ Id: selectedResume?.id, ModifiedBy: Number(currentUserId) }]
      };
      await Postrequestcall(DELETE_USERS_RESUMES, deleteData, loginData?.accessToken);
      setUserResumes(userResumes.filter((resume) => resume.id !== selectedResume?.id));
      setError(TEXT_DELETE_SNACKBAR);
    } catch (error) {
      console.error("confirmDelete-> Error deleting resume: ", error);
      setError("Failed to delete resume. Please try again.");
    } finally {
      setOpenDeleteModal(false);
      setSelectedResume(null);
    }
  };

  const handleOpenResumeDetail = (item) => {
    setOpenResumeDetailsModal(true);
    setSelectedResume(item);
  };

  const handleCloseResumeDetail = () => {
    setOpenResumeDetailsModal(false);
    setSelectedResume(null);
  };

  const openMenu = (event, item) => {
    event.stopPropagation();
    event.preventDefault();
    setMoreMenuAnchorEl(event.currentTarget);
    setSelectedResume(item);
  };

  const handleMoreMenuClose = (event) => {
    event.stopPropagation();
    setMoreMenuAnchorEl(null);
  };

  const renderResumeList = (itemList) => {
    return itemList?.map((item, index) => (
      <Card
        key={index.toString()}
        className="pagelist-content"
        sx={{ marginTop: "0px", padding: 0, cursor: "pointer" }}>
        <ListItem
          alignItems="flex-start"
          sx={{ m: 0, p: 0 }}
          key={index.toString()}
          secondaryAction={
            <IconButton
              onClick={(event) => {
                openMenu(event, item);
              }}>
              <MoreVertIcon />
            </IconButton>
          }>
          <ListItemButton
            alignItems="flex-start"
            sx={{ m: 0 }}
            onClick={() => handleOpenResumeDetail(item)}>
            <ListItemText
              sx={{ m: 0, alignItems: "flex-start" }}
              primary={
                <Typography variant="body1" maxWidth={"430px"} noWrap>
                  {item?.title}
                </Typography>
              }
              secondary={new Date(item?.resumeDate).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric"
              })}
            />
          </ListItemButton>
        </ListItem>
      </Card>
    ));
  };

  return (
    <Box className="main-content-Box" sx={{ p: isSmallScreen ? 2 : 3, pt: 2 }}>
      <Pageheader headerName={headerName} />
      <Card className="main-content-card">
        {isLoading ? (
          <Loader />
        ) : (
          <Box className="main-content-card-list">
            {userResumes.length !== 0 ? (
              <List disablePadding>
                <Stack gap={1}>{renderResumeList(userResumes)}</Stack>
              </List>
            ) : (
              <Box className="empty-data-card">
                <Emptydata
                  icon={<ModeCommentIcon className="emptydata-icon" />}
                  firstText={TEXT_NO_RESUME_LINE1}
                  secondText={TEXT_NO_RESUME_LINE2}
                />
              </Box>
            )}
          </Box>
        )}
        <AddButton toolTipText={TEXT_ADD_RESUME} onAddClick={handleAddClick} />
        <AdContainer />
      </Card>
      {openResumeDetailsModal && (
        <ResumeDetailsModal
          Open={openResumeDetailsModal}
          Close={handleCloseResumeDetail}
          resumeData={selectedResume?.resumePdf}
          title={selectedResume?.title}
          from={"Resume-Builder"}
        />
      )}

      {openDeleteModal && (
        <DeleteConfirmation
          open={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
          headerText={TEXT_CONFIRM_DELETE}
          secondaryText={TEXT_DELETE_THIS_RESUME}
          onBadgeClick={confirmDelete}
          actionBtnText={TEXT_MENU_DELETE_BTN}
        />
      )}

      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError("")}
        message={error}
      />

      <Menu
        style={{ boxShadow: "none" }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        anchorEl={moreMenuAnchorEl}
        open={Boolean(moreMenuAnchorEl)}
        onClose={handleMoreMenuClose}>
        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            handleDeleteResume();
            handleMoreMenuClose(event);
          }}
          style={{ color: "red" }}>
          {TEXT_MENU_DELETE_BTN}
        </MenuItem>
      </Menu>
    </Box>
  );
}
